import React, {useCallback, useMemo, useState} from 'react';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../../src/appStyles/hooks/theme';

import AddPhoto from '../../../../../src/components/core/icon/AddPhoto';
import {Analytics} from '@buncha/config/analytics/analytics';
import {CustomText} from '../../../../../src/components/core/text';
import DeliveryImagesComponent from '../deliveryImagesComponent';
import {HBox} from '@buncha/components/core/view';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../../../../../src/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import PhotoIcon from '../../../../../src/components/core/icon/PhotoIcon';
import {StyleSheet} from 'react-native';
import {Touchable} from '../../../../../src/components/core/touchable';
import {WarningIcon} from 'native-base';
import {useDeliveryDetailsContext} from '../../../../../src/driver/hooks/deliveryDetails';
import {useGetLocale} from '@buncha/localization/hooks';

interface AddDeliveryPhotoCellProps {
  orderId: string;
  deliveryLocationId: number;
  runId: number;
  isRequired?: boolean;
  bagsUsed?: number;
  customerName?: string;
}

const AddDeliveryPhotoCell: React.FC<AddDeliveryPhotoCellProps> = props => {
  const orderId = Number(props?.orderId) || 0;
  const {state} = useDeliveryDetailsContext();
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const deliveryLocationId = props?.deliveryLocationId || 0;
  const [open, setOpen] = useState(false);
  const deliveryImagesAdded = state?.deliveryImages?.length;
  const {body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const toggleImagesComponent = useCallback(() => {
    setOpen(!open);
    if (open)
      Analytics.eventWithProps('Add delivery photos', {
        type: 'CTA',
        userType: 'driver',
      });
  }, [open]);

  const getCellText = useMemo(() => {
    if (deliveryImagesAdded) return body?.picsAdded;
    return body?.addPics;
  }, [body?.addPics, body?.picsAdded, deliveryImagesAdded]);

  return (
    <React.Fragment>
      <Touchable
        style={[
          styles.parentBox,
          {
            borderColor: theme?.text.gray[100],
            backgroundColor: deliveryImagesAdded
              ? theme?.background.blue[100]
              : theme?.background.white[100],
          },
        ]}
        onPress={toggleImagesComponent}>
        <If condition={props.isRequired && !deliveryImagesAdded}>
          <WarningIcon
            style={styles.warningIcon}
            color={theme?.icon.red[100]}
          />
        </If>
        <If condition={!deliveryImagesAdded}>
          <AddPhoto
            height={24}
            width={24}
            fill={theme?.icon?.blue?.[300]}
            style={styles.icon}
            viewBox="-1 2 39 30"
          />
        </If>
        <If condition={deliveryImagesAdded}>
          <PhotoIcon
            height={24}
            width={24}
            fill={theme?.icon?.white?.[100]}
            style={styles.icon}
            viewBox="-1 2 46 30"
          />
        </If>

        <HBox style={styles.addedContainer}>
          <CustomText
            style={[
              styles.text,
              {
                color: deliveryImagesAdded
                  ? theme?.text?.white?.[100]
                  : theme?.text?.blue?.[300],
                fontFamily: muli?.[700]?.normal,
              },
            ]}>
            {getCellText}
          </CustomText>
        </HBox>
      </Touchable>
      <DeliveryImagesComponent
        isOpen={open}
        onClose={toggleImagesComponent}
        orderId={orderId}
        deliveryLocationId={deliveryLocationId}
        runId={props?.runId}
        bagsUsed={props?.bagsUsed}
        customerName={props?.customerName}
      />
    </React.Fragment>
  );
};

export default React.memo(AddDeliveryPhotoCell);

const styles = StyleSheet.create({
  parentBox: {
    justifyContent: 'center',
    paddingHorizontal: 2,
    paddingVertical: 15,
    borderRadius: 8,
    flex: 1,
    borderWidth: 0.5,
  },
  text: {
    marginTop: 5,
  },
  icon: {
    alignSelf: 'center',
  },
  warningIcon: {position: 'absolute', right: 2, top: 2},
  addedContainer: {justifyContent: 'center', alignItems: 'center', gap: 2},
});
